import React from 'react';

import { RootComponentInstance } from '@uniformdev/canvas';
import { parse } from 'cookie';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { v4 as uuidv4 } from 'uuid';

import { initUFO } from '@/analytics/react-ufo/initUFO';
import { getAnalyticsWebClient } from '@/analytics/wac-analytics/get-analytics-web-client';
import { IntercomProperties, IntercomSettingsProperties } from '@/libraries/intercom/src/types';
import {
	extractOptanonConsentCookie,
	isPerformanceOnetrustEnabled,
} from '@/utils/onetrust-provider';

import {
	canonicalPathForHref,
	getCanonicalBaseUrl,
} from './bifrost-template/get-canonical-base-url';
import { TemplateCategory } from './components/confluence/templates/template-categories-search';
import { TemplateCollection, type Template } from './content/templates/template.types';
import { AppPricingData } from './contentful/product-catalog';
import { initializeFeatureGatesClient } from './feature-gates/initialization';
import {
	AvailableProductsResponse,
	CcpTransactionAccountData,
	ServerSideRequestStatus,
	UserProfile,
	UserProfileResponse,
} from './libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { GrowthUnifiedProfile } from './libraries/growth-unified-profile-client/unified-profile.schema';
import { PageWrapper } from './page-wrapper-streaming';
import { AuxiaResponse } from './pages/software/jira/service-management/auxia/get-auxia-jsm-paid-lp-treatment';
import { configureCastle } from './utils/castle';
import { DynamicConfigClient } from './utils/dynamic-configs/dynamic-config-client/dynamic-config-client';
import {
	overrideAjsAnonIdFromCookie,
	overrideCustomAttributes,
	isEdgeCachingPath,
} from './utils/fg-bootstrap-values-override-utils';
import { GridOverlayFunction } from './utils/grid-overlay';
import { SentryIntlProvider } from './utils/sentry-intl';
import { LocationData, UserLocale } from './utils/useGeoCurrency';
import { processExperimentAndFeatureGateOverrides } from './utils/wac-browser-utility';
import { parseRoute } from './wac-routes';

window.BUILD_KEY = process.env['VERSION'];
window.featureGateBootstrapValues = window.featureGateBootstrapValues || {
	identifiers: {
		analyticsAnonymousId: uuidv4(),
	},
};

configureCastle();

const SUPPORTED_LOCALES = [
	{ hrefLang: 'ja', langRoute: 'ja' },
	{ hrefLang: 'fr', langRoute: 'fr' },
	{ hrefLang: 'de', langRoute: 'de' },
	{ hrefLang: 'es', langRoute: 'es' },
	{ hrefLang: 'pt-BR', langRoute: 'br' },
	{ hrefLang: 'zh-Hans', langRoute: 'zh' },
	{ hrefLang: 'ko', langRoute: 'ko' },
	{ hrefLang: 'ru', langRoute: 'ru' },
	{ hrefLang: 'pl', langRoute: 'pl' },
	{ hrefLang: 'it', langRoute: 'it' },
	{ hrefLang: 'nl', langRoute: 'nl' },
];

declare global {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore duplicate
	type FeatureFlagType = 'boolean' | 'string' | 'number';

	interface Window {
		IS_STATIC?: boolean;
		BUILD_KEY?: string;
		entryName?: string;
		SSR_languagePack: Record<string, string>;
		prices?: AppPricingData;
		availableProducts?: AvailableProductsResponse;
		userProfile?: UserProfile;
		userProfileStatus?: ServerSideRequestStatus;
		userContext?: UserProfileResponse;
		userContextStatus?: ServerSideRequestStatus;
		loggedOutContext?: GrowthUnifiedProfile;
		loggedOutContextStatus?: ServerSideRequestStatus;
		loggedInContext?: GrowthUnifiedProfile;
		loggedInContextStatus?: ServerSideRequestStatus;
		cmsPageVariant?: string;
		templates?: Template[];
		mlTemplateIds?: string[];
		partnerTemplates?: Template[];
		collections?: TemplateCollection[];
		categories?: TemplateCategory[];
		teamType?: string;
		keyword?: string;
		featureFlags: Record<string, FeatureFlagType>;
		countryCode?: UserLocale;
		regionIsoCode?: string;
		optanonConsentCookie?: string;
		AtlOneTrustWrapper?: { updatePolicyLinks: () => void };
		Intercom?: IntercomProperties;
		intercomSettings?: IntercomSettingsProperties;
		toggleGrid?: GridOverlayFunction;
		isUniformPlayground?: boolean;
		isEdgeRoutedToWae?: boolean;
		isAllowPostMessage?: boolean;
		s2sExperimentCallMade?: boolean;
		referrer?: string;
		track?: string;
		useEsmodules?: boolean;
		channel?: string;
		anchor?: string;
		subAnchor?: string;
		auxiaResponse?: AuxiaResponse | null;
		ssrAnonId?: string;
		clusterTag?: string;
		_uniformPreloadedComposition?: RootComponentInstance;
		_uniformRegenerationMetadata?: {
			id: string;
			name: string;
			modified: string;
			generationTime: number;
			trace: string;
		};
		transactionAccounts?: Array<CcpTransactionAccountData>;
	}
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const init = (Component: React.ComponentType<any>) => {
	const isStatic = typeof window !== 'undefined' && window.IS_STATIC;
	const analyticsWebClient = getAnalyticsWebClient();
	const { locale, path } = parseRoute(window.location);
	// Get location data for experiments and feature gates from server-provided data
	let locationData: LocationData | undefined;

	// Use server-provided data (no client-side API calls)

	if (typeof window !== 'undefined') {
		locationData = {
			countryIsoCode: window.countryCode || '',
			regionIsoCode: window.regionIsoCode || '',
		};
	}

	// In Uniform Playground (preview authoring environment), skip OneTrust check
	// and assume performance tracking is enabled — mirrors OneTrustProvider behavior.
	// Cookies are unavailable in the cross-origin iframe context, so the normal
	// cookie-based check always returns false for CPRA users (e.g. California).
	const isPerfOnetrustEnabled =
		window.isUniformPlayground ||
		isPerformanceOnetrustEnabled(
			typeof window !== 'undefined'
				? window.optanonConsentCookie
				: extractOptanonConsentCookie(document.cookie),
			locationData,
		);
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	isPerfOnetrustEnabled && initUFO(analyticsWebClient, path);
	const parsedClientCookies = document.cookie ? parse(document.cookie) : {};
	const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : '';

	const { finalExperimentOverrides, finalFeatureGateOverrides } =
		processExperimentAndFeatureGateOverrides(parsedClientCookies, userAgent, isPerfOnetrustEnabled);

	// Below is for debugging edge - will be removed
	window.ssrAnonId = window.featureGateBootstrapValues.identifiers.analyticsAnonymousId;

	// We can't feature gate below code as this is done before initialization so just overriding for the path we are testing
	if (isEdgeCachingPath(window.location)) {
		// Override for Edge Caching
		overrideAjsAnonIdFromCookie();
		overrideCustomAttributes();
	}

	initializeFeatureGatesClient(window.featureGateBootstrapValues, {
		overrideValues: {
			experimentOverrides: finalExperimentOverrides,
			featureGateOverrides: finalFeatureGateOverrides,
		},
		// if performance cookies are disabled, we will skip exposures
		localMode: !isPerfOnetrustEnabled,
	}).then(async () => {
		try {
			await DynamicConfigClient.getInstance().initialize({
				bootstrapValues: window.featureGateBootstrapValues,
			});
		} catch (error) {
			console.error('Failed to initialize DynamicConfigClient', error);
		}

		const canonicalBaseUrl = getCanonicalBaseUrl(window.location.hostname, path);
		const pathForCanonical = path ? canonicalPathForHref(canonicalBaseUrl, path) : undefined;
		const route =
			pathForCanonical === undefined ? '' : pathForCanonical === '' ? '' : `/${pathForCanonical}`;
		const page = (
			<SentryIntlProvider
				locale={locale}
				messages={window.SSR_languagePack}
				cookies={document.cookie}
			>
				{isStatic && <link rel="canonical" href={`${canonicalBaseUrl}${route}`} />}
				{isStatic && (
					<link rel="alternate" href={`${canonicalBaseUrl}${route}`} hrefLang="x-default" />
				)}
				{isStatic &&
					SUPPORTED_LOCALES.map(({ hrefLang, langRoute }) => (
						<link
							key={hrefLang}
							rel="alternate"
							href={`${canonicalBaseUrl}/${langRoute}${route}`}
							hrefLang={hrefLang}
						/>
					))}
				<PageWrapper
					analyticsWebClient={analyticsWebClient}
					location={window.location}
					pricing={window.prices}
					availableProducts={window.availableProducts}
					userProfile={window.userProfile}
					userProfileStatus={window.userProfileStatus}
					userContext={window.userContext}
					userContextStatus={window.userContextStatus}
					loggedOutContext={window.loggedOutContext}
					loggedOutContextStatus={window.loggedOutContextStatus}
					loggedInContext={window.loggedInContext}
					loggedInContextStatus={window.loggedInContextStatus}
					countryCode={window.countryCode}
					templates={window.templates}
					partnerTemplates={window.partnerTemplates}
					collections={window.collections}
					categories={window.categories}
					composition={window._uniformPreloadedComposition}
					compositionRegenerationMetadata={window._uniformRegenerationMetadata}
					isUniformPlayground={window.isUniformPlayground}
					isAllowPostMessage={window.isAllowPostMessage}
					teamType={window.teamType}
					keyword={window.keyword}
					referrer={window.referrer}
					mlTemplateIds={window.mlTemplateIds}
					channel={window.channel}
					track={window.track}
					anchor={window.anchor}
					subAnchor={window.subAnchor}
					auxiaResponse={window.auxiaResponse}
					PageComponent={Component}
					transactionAccounts={window.transactionAccounts}
					userAgent={userAgent}
					clusterTag={window.clusterTag}
				/>
			</SentryIntlProvider>
		);

		if (isStatic) {
			// @ts-expect-error type
			const root = createRoot(document.getElementById('wac'));
			root.render(page);
		} else {
			// @ts-expect-error type
			hydrateRoot(document.getElementById('wac-root'), page, {
				onRecoverableError: (error, errorInfo) => {
					// add an attribute if in CI
					if (process.env['CI'] === 'true') {
						document.getElementById('wac-root')?.setAttribute('hydration-error', 'true');
					}
					if (process.env['MODE'] === 'dev') {
						console.log(`Recoverable Error (including Hydration): ${error}`, errorInfo);
					}
				},
			});
		}
	});
};
